import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CalendarDays, Clock, Phone, Send } from "lucide-react";
import axios from "axios";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScheduleVisitPage() {
  const [searchParams] = useSearchParams();
  const propertyId = searchParams.get("property") || "";
  const propertyTitle = searchParams.get("title") || "";

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: propertyTitle ? `Site visit for: ${propertyTitle}` : "",
  });

  useEffect(() => {
    if (!selectedDate) return;
    const fetchSlots = async () => {
      setLoadingSlots(true);
      setSelectedTime("");
      try {
        const dateStr = format(selectedDate, "yyyy-MM-dd");
        const res = await axios.get(`${API}/bookings/slots`, { params: { date: dateStr } });
        setAvailableSlots(res.data.available || []);
        setBookedSlots(res.data.booked || []);
      } catch (e) {
        console.error(e);
        setAvailableSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    };
    fetchSlots();
  }, [selectedDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) {
      toast.error("Please select a date and time slot");
      return;
    }
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...form,
        property_id: propertyId,
        property_title: propertyTitle,
        visit_date: format(selectedDate, "yyyy-MM-dd"),
        visit_time: selectedTime,
      });
      toast.success("Visit scheduled! Redirecting to WhatsApp...");
      setSubmitted(true);

      setTimeout(() => {
        const waMsg = encodeURIComponent(
          `Hi, I'm ${form.name}. I've scheduled a site visit for ${format(selectedDate, "MMM dd, yyyy")} at ${selectedTime}.${propertyTitle ? `\nProperty: ${propertyTitle}` : ""}\nPhone: ${form.phone}`
        );
        window.open(`https://wa.me/918483918695?text=${waMsg}`, "_blank");
      }, 1500);
    } catch (e) {
      toast.error("Failed to schedule visit. Please try again.");
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const disabledDays = { before: new Date() };

  return (
    <div data-testid="schedule-visit-page" className="pt-24 pb-16 min-h-screen">
      <div className="px-6 lg:px-8 max-w-5xl mx-auto">
        <div className="mb-10">
          <p className="font-cursive text-gold text-3xl mb-2">Schedule a Visit</p>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter text-beige mb-3">
            Book Your Property Tour
          </h1>
          <p className="text-beige/60 max-w-lg">
            Choose a convenient date and time. We'll confirm your visit and send you all the details.
          </p>
        </div>

        {propertyTitle && (
          <div className="mb-8 p-4 bg-navy-light border border-gold/20 rounded-sm inline-block">
            <p className="text-xs text-gold mb-1 tracking-wider uppercase">Visiting Property</p>
            <p className="text-beige font-heading">{propertyTitle}</p>
          </div>
        )}

        {submitted ? (
          <div data-testid="booking-success-message" className="bg-navy-light border border-gold/30 rounded-sm p-12 text-center max-w-lg mx-auto">
            <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CalendarDays size={28} className="text-gold" />
            </div>
            <h3 className="font-heading text-2xl text-beige mb-3">Visit Scheduled!</h3>
            <p className="text-beige/60 mb-2">
              {format(selectedDate, "MMMM dd, yyyy")} at {selectedTime}
            </p>
            <p className="text-beige/50 text-sm mb-6">
              We'll reach out to confirm. You can also chat with us on WhatsApp.
            </p>
            <button
              onClick={() => {
                setSubmitted(false);
                setSelectedDate(null);
                setSelectedTime("");
                setForm({ name: "", email: "", phone: "", message: "" });
              }}
              data-testid="book-another-visit-button"
              className="text-gold text-sm hover:text-gold-hover transition-colors"
            >
              Schedule another visit
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} data-testid="booking-form">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left: Calendar + Time Slots */}
              <div className="space-y-6">
                {/* Calendar */}
                <div className="bg-navy-light border border-white/10 rounded-sm p-6">
                  <h3 className="flex items-center gap-2 text-sm text-gold tracking-wider uppercase font-bold mb-4">
                    <CalendarDays size={16} /> Select Date
                  </h3>
                  <div className="flex justify-center [&_.rdp]:text-beige [&_.rdp-day_button]:text-beige [&_.rdp-day_button:hover]:bg-gold/20 [&_.rdp-day_button.rdp-day_selected]:bg-gold [&_.rdp-day_button.rdp-day_selected]:text-navy [&_.rdp-button]:text-beige [&_.rdp-head_cell]:text-gold/60 [&_.rdp-caption_label]:text-beige [&_.rdp-nav_button]:text-gold [&_.rdp-day_button.rdp-day_disabled]:text-beige/20 [&_.rdp-day_button.rdp-day_disabled:hover]:bg-transparent">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={disabledDays}
                      data-testid="visit-calendar"
                      className="rounded-sm"
                    />
                  </div>
                </div>

                {/* Time Slots */}
                {selectedDate && (
                  <div className="bg-navy-light border border-white/10 rounded-sm p-6">
                    <h3 className="flex items-center gap-2 text-sm text-gold tracking-wider uppercase font-bold mb-4">
                      <Clock size={16} /> Select Time — {format(selectedDate, "MMM dd")}
                    </h3>
                    {loadingSlots ? (
                      <div className="text-beige/40 text-sm py-4 text-center">Loading available slots...</div>
                    ) : availableSlots.length === 0 ? (
                      <div className="text-beige/40 text-sm py-4 text-center">No slots available for this date. Try another date.</div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {availableSlots.map((slot) => (
                          <button
                            key={slot}
                            type="button"
                            data-testid={`time-slot-${slot.replace(/[\s:]/g, "-")}`}
                            onClick={() => setSelectedTime(slot)}
                            className={`text-sm py-3 px-2 border transition-all duration-200 rounded-sm ${
                              selectedTime === slot
                                ? "bg-gold text-navy border-gold font-bold"
                                : "border-white/10 text-beige/70 hover:border-gold/40 hover:text-beige"
                            }`}
                          >
                            {slot}
                          </button>
                        ))}
                      </div>
                    )}
                    {bookedSlots.length > 0 && (
                      <p className="text-xs text-beige/30 mt-3">{bookedSlots.length} slot(s) already booked</p>
                    )}
                  </div>
                )}
              </div>

              {/* Right: Contact Form */}
              <div className="bg-navy-light border border-white/10 rounded-sm p-6 h-fit">
                <h3 className="text-sm text-gold tracking-wider uppercase font-bold mb-5">Your Details</h3>

                {selectedDate && selectedTime && (
                  <div className="mb-5 p-3 bg-navy border border-gold/20 rounded-sm">
                    <p className="text-xs text-gold mb-1">Selected Slot</p>
                    <p className="text-sm text-beige">{format(selectedDate, "MMMM dd, yyyy")} at {selectedTime}</p>
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-beige/50 mb-1 block tracking-wider uppercase">Full Name *</label>
                    <Input
                      data-testid="booking-name-input"
                      value={form.name}
                      onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                      placeholder="Your name"
                      className="bg-navy border-white/10 text-beige placeholder:text-beige/30 rounded-sm h-11 focus:border-gold/50"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs text-beige/50 mb-1 block tracking-wider uppercase">Email *</label>
                    <Input
                      data-testid="booking-email-input"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                      placeholder="your@email.com"
                      className="bg-navy border-white/10 text-beige placeholder:text-beige/30 rounded-sm h-11 focus:border-gold/50"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs text-beige/50 mb-1 block tracking-wider uppercase">Phone *</label>
                    <Input
                      data-testid="booking-phone-input"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                      placeholder="+91 XXXXX XXXXX"
                      className="bg-navy border-white/10 text-beige placeholder:text-beige/30 rounded-sm h-11 focus:border-gold/50"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs text-beige/50 mb-1 block tracking-wider uppercase">Message</label>
                    <Textarea
                      data-testid="booking-message-input"
                      value={form.message}
                      onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
                      placeholder="Any special requests..."
                      className="bg-navy border-white/10 text-beige placeholder:text-beige/30 rounded-sm min-h-[80px] focus:border-gold/50"
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="booking-submit-button"
                    disabled={submitting || !selectedDate || !selectedTime}
                    className="w-full bg-gold hover:bg-gold-hover text-navy font-bold text-sm tracking-widest uppercase px-6 py-4 transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {submitting ? "Scheduling..." : <><Send size={16} /> Schedule & Notify via WhatsApp</>}
                  </button>

                  <a
                    href="https://wa.me/918483918695"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="booking-whatsapp-link"
                    className="flex items-center justify-center gap-2 w-full border border-gold/50 hover:border-gold text-gold text-sm tracking-widest uppercase px-6 py-3 transition-colors"
                  >
                    <Phone size={14} /> Chat on WhatsApp Instead
                  </a>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
